import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Loader2,
  Play,
  RefreshCw,
  Save,
  ShieldCheck,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import { bridgeChromeToolEvents, useAgentActivity } from '@/webmcp/agentActivity';
import { AgentActivityDock } from '@/webmcp/ui/AgentActivityDock';
import { AgentSurfaceIntro } from '@/webmcp/ui/AgentSurfaceIntro';

import {
  useGenerateKeywordFeedItem,
  useCreateKeywordVettingJob,
  useKeywordFeed,
  useKeywordFeedConfig,
  useRefreshKeywordFeed,
  useRefreshKeywordFeedFromAhrefs,
  useUpdateKeywordFeedConfig,
  useUpdateKeywordFeedItem,
  useKeywordVettingJob,
} from '@/hooks/useContentPlannerAPI';
import { useBlogTemplates } from '@/hooks/useBlogTemplates';
import { getKeywordVettingErrorMessage } from '@/services/contentPlannerService';
import { useTenantStore } from '@/stores/useTenantStore';
import type {
  KeywordFeedConfig,
  KeywordFeedDuplicateStatus,
  KeywordFeedHermesVerdict,
  KeywordFeedItem,
  KeywordFeedStatus,
} from '@/types/contentPlanner';

interface KeywordFeedPanelProps {
  search?: string;
}

const FEED_WRITER_MODEL = 'qwen3.8-flash';

const STATUS_LABELS: Array<{ value: '' | KeywordFeedStatus; label: string }> = [
  { value: '', label: 'All active' },
  { value: 'candidate', label: 'Candidates' },
  { value: 'approved', label: 'Approved' },
  { value: 'queued', label: 'Queued' },
  { value: 'generated', label: 'Generated' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'rejected', label: 'Rejected' },
];

const STATUS_COLORS: Record<KeywordFeedStatus, { bg: string; color: string }> = {
  candidate: { bg: 'var(--color-paper-2)', color: 'var(--color-ink-2)' },
  approved: { bg: 'rgba(20, 148, 136, 0.12)', color: 'var(--color-teal)' },
  rejected: { bg: 'rgba(220, 38, 38, 0.10)', color: '#b91c1c' },
  queued: { bg: 'rgba(217, 119, 6, 0.12)', color: 'var(--color-coral-dark)' },
  generated: { bg: 'rgba(20, 148, 136, 0.16)', color: 'var(--color-teal)' },
  skipped: { bg: 'rgba(107, 114, 128, 0.12)', color: 'var(--color-ink-2)' },
  stale: { bg: 'rgba(217, 119, 6, 0.12)', color: 'var(--color-coral-dark)' },
};

const DUPLICATE_LABELS: Record<KeywordFeedDuplicateStatus, string> = {
  clear: 'Clear',
  exact_duplicate: 'Exact match',
  near_duplicate: 'Near match',
};

const VETTING_LABELS: Record<KeywordFeedHermesVerdict, string> = {
  agree: 'Codex: agree',
  weak: 'Codex: weak',
  swap: 'Codex: swap',
};

const VETTING_COLORS: Record<KeywordFeedHermesVerdict, { bg: string; color: string }> = {
  agree: { bg: 'rgba(20, 148, 136, 0.12)', color: 'var(--color-teal)' },
  weak: { bg: 'rgba(217, 119, 6, 0.12)', color: 'var(--color-coral-dark)' },
  swap: { bg: 'hsl(var(--destructive) / 0.12)', color: 'hsl(var(--destructive))' },
};

const DEFAULT_FEED_CONFIG: KeywordFeedConfig = {
  enabled: true,
  max_feed_items: 50,
  max_candidates: 250,
  similarity_threshold: 0.58,
  min_opportunity_score: null,
  include_cluster_statuses: ['new', 'approved'],
  auto_refresh_enabled: false,
  ahrefs_seeds: [],
  ahrefs_country: 'us',
  ahrefs_target_count: 30,
  ahrefs_per_seed_limit: 50,
  ahrefs_min_volume: 0,
  ahrefs_max_difficulty: null,
  ahrefs_preferred_keywords: [],
  ahrefs_exclude_terms: [],
};

function parseKeywordList(value: string): string[] {
  const seen = new Set<string>();
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean)
    .filter((item) => {
      const key = item.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function formatNumber(value: number | null | undefined): string {
  return typeof value === 'number' ? value.toLocaleString() : '0';
}

function formatPercent(value: number | null | undefined): string {
  if (typeof value !== 'number') return '0%';
  return `${Math.round(value * 100)}%`;
}

export function selectKeywordVettingItems(items: KeywordFeedItem[]): KeywordFeedItem[] {
  return items
    .filter((item) =>
      item.duplicate_status === 'clear' &&
      !item.hermes_verdict &&
      (item.status === 'candidate' || item.status === 'approved'))
    .slice(0, 100);
}

/** 'YYYY-MM' -> 'Jul 2026'. Returns null for missing/malformed values. */
function formatTargetMonth(value: string | null | undefined): string | null {
  const match = value ? /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value) : null;
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function StatusChip({ status }: { status: KeywordFeedStatus }) {
  const colors = STATUS_COLORS[status];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        padding: '0 8px',
        borderRadius: 999,
        background: colors.bg,
        color: colors.color,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'capitalize',
      }}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

function DuplicateChip({ item }: { item: KeywordFeedItem }) {
  const isClear = item.duplicate_status === 'clear';
  return (
    <span
      title={item.filter_reason ?? undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        height: 22,
        padding: '0 8px',
        borderRadius: 999,
        background: isClear ? 'rgba(20, 148, 136, 0.10)' : 'rgba(217, 119, 6, 0.12)',
        color: isClear ? 'var(--color-teal)' : 'var(--color-coral-dark)',
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {!isClear && <AlertTriangle size={12} strokeWidth={2} />}
      {DUPLICATE_LABELS[item.duplicate_status]}
    </span>
  );
}

/**
 * Content-planning month bucket (e.g. "Jul 2026"). Advisory display only —
 * never gates Approve/Generate. Shows a muted "Unplanned" label when no
 * target_month is set yet.
 */
function MonthChip({ value }: { value?: string | null }) {
  const label = formatTargetMonth(value);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        padding: '0 8px',
        borderRadius: 999,
        background: label ? 'var(--color-teal-light)' : 'var(--color-paper-2)',
        color: label ? 'var(--color-teal-dark)' : 'var(--color-ink-3)',
        fontSize: 11,
        fontWeight: 700,
        fontStyle: label ? 'normal' : 'italic',
      }}
    >
      {label ?? 'Unplanned'}
    </span>
  );
}

/**
 * Advisory Codex/Hermes vetting indicator. Never changes item status — a
 * SWAP verdict only surfaces a confirm dialog on Approve (see handleApprove).
 * Unvetted items show a muted dot rather than no indicator, so it's clear
 * vetting hasn't run yet vs. having run and agreed.
 */
function VettingBadge({ item }: { item: KeywordFeedItem }) {
  const verdict = item.hermes_verdict;
  if (!verdict) {
    return (
      <span
        title="Not yet vetted by Codex"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          height: 22,
          padding: '0 8px',
          borderRadius: 999,
          background: 'var(--color-paper-2)',
          color: 'var(--color-ink-3)',
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'var(--color-ink-3)',
          }}
        />
        Unvetted
      </span>
    );
  }

  const colors = VETTING_COLORS[verdict];
  return (
    <span
      title={item.hermes_reason ?? undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        height: 22,
        padding: '0 8px',
        borderRadius: 999,
        background: colors.bg,
        color: colors.color,
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {verdict === 'swap' && <AlertTriangle size={12} strokeWidth={2} />}
      {VETTING_LABELS[verdict]}
    </span>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
  agentTarget,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  /** Marks this control as agent-drivable so the intro can outline it. */
  agentTarget?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      data-agent-target={agentTarget}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'grid',
        placeItems: 'center',
        width: 30,
        height: 30,
        borderRadius: 7,
        border: '1px solid var(--color-warm-border)',
        background: disabled ? 'var(--color-paper-2)' : 'var(--color-paper)',
        color: disabled ? 'var(--color-ink-3)' : 'var(--color-ink-2)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function FeedRow({
  item,
  onApprove,
  onReject,
  onGenerate,
  onMonthChange,
  busy,
  loadingItemId,
}: {
  item: KeywordFeedItem;
  onApprove: (item: KeywordFeedItem) => void;
  onReject: (item: KeywordFeedItem) => void;
  onGenerate: (item: KeywordFeedItem) => void;
  onMonthChange: (item: KeywordFeedItem, value: string) => void;
  busy: boolean;
  loadingItemId?: string;
}) {
  const canGenerate =
    item.duplicate_status === 'clear' &&
    item.status === 'approved';
  const sourceTitle = item.evidence?.similarity_source?.title;
  const isRowLoading = loadingItemId === item.id;
  const rowDisabled = busy || isRowLoading;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(240px, 2fr) 100px 88px 88px 128px 118px 120px',
        alignItems: 'center',
        gap: 14,
        minWidth: 980,
        padding: '12px 16px',
        borderBottom: '1px solid var(--color-warm-border)',
        background: 'var(--color-paper)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            color: 'var(--color-ink)',
            fontSize: 13,
            fontWeight: 650,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={item.primary_keyword}
        >
          {item.primary_keyword}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 4,
            color: 'var(--color-ink-3)',
            fontSize: 11.5,
            minWidth: 0,
          }}
        >
          <span>{item.map_name || 'Research'}</span>
          {item.secondary_keywords.length > 0 && (
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.secondary_keywords.slice(0, 3).join(', ')}
            </span>
          )}
        </div>
        {sourceTitle && item.duplicate_status !== 'clear' && (
          <div
            style={{
              marginTop: 4,
              color: 'var(--color-coral-dark)',
              fontSize: 11,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={sourceTitle}
          >
            Overlaps: {sourceTitle}
          </div>
        )}
        {item.hermes_verdict === 'swap' && item.hermes_suggested_swap && (
          <div
            style={{
              marginTop: 4,
              color: 'hsl(var(--destructive))',
              fontSize: 11,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={item.hermes_reason ?? undefined}
          >
            Codex suggests: {item.hermes_suggested_swap}
          </div>
        )}
      </div>

      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-ink)' }}>
          {item.opportunity_score}
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-ink-3)' }}>opportunity</div>
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 650, color: 'var(--color-ink)' }}>
          {formatNumber(item.total_volume)}
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-ink-3)' }}>volume</div>
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 650, color: 'var(--color-ink)' }}>
          {item.avg_difficulty}
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-ink-3)' }}>difficulty</div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-start' }}>
        <StatusChip status={item.status} />
        <DuplicateChip item={item} />
        <VettingBadge item={item} />
        <span style={{ color: 'var(--color-ink-3)', fontSize: 11 }}>
          {formatPercent(item.similarity_score)} overlap
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-start' }}>
        <MonthChip value={item.target_month} />
        <input
          type="month"
          aria-label={`Content month for ${item.primary_keyword}`}
          data-agent-target="feed.month"
          value={item.target_month ?? ''}
          disabled={rowDisabled}
          onChange={(event) => {
            const value = event.target.value;
            if (value) onMonthChange(item, value);
          }}
          style={{
            width: '100%',
            height: 24,
            borderRadius: 6,
            border: '1px solid var(--color-warm-border)',
            background: 'var(--color-paper-2)',
            color: 'var(--color-ink-2)',
            fontSize: 11,
            padding: '0 6px',
          }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <IconButton
          label="Approve"
          agentTarget="feed.approve"
          disabled={rowDisabled || item.status === 'approved'}
          onClick={() => onApprove(item)}
        >
          {isRowLoading ? (
            <Loader2 size={15} strokeWidth={2} className="animate-spin" style={{ color: 'var(--color-teal)' }} />
          ) : (
            <ShieldCheck size={15} strokeWidth={2} />
          )}
        </IconButton>
        <IconButton
          label="Reject"
          disabled={rowDisabled || item.status === 'rejected'}
          onClick={() => onReject(item)}
        >
          <X size={15} strokeWidth={2} />
        </IconButton>
        <IconButton
          label={
            item.duplicate_status !== 'clear'
              ? 'Generate (duplicate blocked)'
              : item.status !== 'approved'
                ? 'Approve first to generate'
                : 'Generate'
          }
          disabled={rowDisabled || !canGenerate}
          onClick={() => onGenerate(item)}
        >
          {isRowLoading ? (
            <Loader2 size={15} strokeWidth={2} className="animate-spin" style={{ color: 'var(--color-teal)' }} />
          ) : (
            <Play size={15} strokeWidth={2} />
          )}
        </IconButton>
      </div>
    </div>
  );
}

export const KeywordFeedPanel: React.FC<KeywordFeedPanelProps> = ({ search = '' }) => {
  const { currentTenant } = useTenantStore();
  const tenantId = currentTenant || null;
  const [includeSkipped, setIncludeSkipped] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'' | KeywordFeedStatus>('');
  // '' = no explicit choice; the request omits template_id and the backend
  // applies its own default recipe.
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [seedText, setSeedText] = useState('');
  const [excludeText, setExcludeText] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [ahrefsDryRun, setAhrefsDryRun] = useState(true);
  const [country, setCountry] = useState('us');
  const [targetCount, setTargetCount] = useState(30);
  const [perSeedLimit, setPerSeedLimit] = useState(50);
  const [minVolume, setMinVolume] = useState(0);
  const [maxDifficulty, setMaxDifficulty] = useState('');
  const [loadingItemId, setLoadingItemId] = useState<string | undefined>(undefined);

  // WebMCP surface. Registers what an agent can actually drive here, bridges
  // Chrome's own routing event, and narrates the approve tool's handshake so
  // the dock and the bubble tell the same story from the same store.
  const agent = useAgentActivity();
  useEffect(() => {
    agent.registerTouchables([
      {
        key: 'feed.approve',
        label: 'Approve',
        can: 'approve candidates (planner_approve_keywords)',
      },
      {
        key: 'feed.month',
        label: 'Content month',
        can: 'read the planning bucket (planner_find_keywords)',
      },
    ]);
    const disposeChrome = bridgeChromeToolEvents();

    // The approve tool fires this after its write lands, which is the only
    // moment the row state is real. Anything earlier would narrate a write that
    // might still refuse.
    const onApproved = (event: Event) => {
      const ids = (event as CustomEvent<{ ids?: string[] }>).detail?.ids ?? [];
      const count = ids.length;
      agent.settle(
        count
          ? `Approved ${count} keyword${count === 1 ? '' : 's'} from the feed.`
          : 'Approved the selected keywords.',
      );
      window.setTimeout(() => agent.reset(), 2600);
    };
    window.addEventListener('planner-keywords-approved', onApproved);

    return () => {
      disposeChrome();
      window.removeEventListener('planner-keywords-approved', onApproved);
      agent.clearTouchables(['feed.approve', 'feed.month']);
    };
    // Store actions are stable; re-running would re-register on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [activeVettingJob, setActiveVettingJob] = useState<{
    jobId: string;
    tenantId: string;
  } | null>(null);
  const reportedVettingTerminalRef = useRef<string | null>(null);

  const params = useMemo(
    () => ({
      include_skipped: includeSkipped,
      status: statusFilter || undefined,
      limit: 100,
    }),
    [includeSkipped, statusFilter],
  );

  const { data, isLoading, isError, refetch } = useKeywordFeed(tenantId, params);
  const { data: feedConfig } = useKeywordFeedConfig(tenantId);
  const refreshMutation = useRefreshKeywordFeed();
  const ahrefsRefreshMutation = useRefreshKeywordFeedFromAhrefs();
  const configMutation = useUpdateKeywordFeedConfig();
  const updateMutation = useUpdateKeywordFeedItem();
  const generateMutation = useGenerateKeywordFeedItem();
  const vettingMutation = useCreateKeywordVettingJob();
  const {
    data: templatesData,
    isLoading: templatesLoading,
    isError: templatesError,
  } = useBlogTemplates();
  const currentTenantVettingJob = activeVettingJob?.tenantId === tenantId
    ? activeVettingJob
    : null;
  const vettingJobQuery = useKeywordVettingJob(
    currentTenantVettingJob?.tenantId ?? null,
    currentTenantVettingJob?.jobId ?? null,
  );
  const effectiveConfig = feedConfig ?? DEFAULT_FEED_CONFIG;

  /**
   * System recipes only. GET /blog-templates is not tenant-scoped, so listing
   * every template would leak other tenants' custom recipes into this dropdown;
   * gate_conflict recipes are held back from advertising. `is_active` is
   * deliberately not filtered here — it is absent from BlogTemplateSummary and
   * the list endpoint already excludes inactive templates (include_inactive=false).
   */
  const templateOptions = useMemo(
    () => (templatesData?.templates ?? []).filter((t) => t.is_system && !t.gate_conflict),
    [templatesData],
  );

  // A template id belongs to the tenant it was picked under. Switching clients
  // re-keys the templates query, so drop the stale pick rather than let Generate
  // send a foreign-tenant id.
  useEffect(() => {
    setSelectedTemplateId('');
  }, [tenantId]);

  useEffect(() => {
    setActiveVettingJob((job) => {
      if (!job || job.tenantId === tenantId) return job;
      reportedVettingTerminalRef.current = null;
      return null;
    });
  }, [activeVettingJob?.tenantId, tenantId]);

  useEffect(() => {
    if (!feedConfig) return;
    setSeedText(feedConfig.ahrefs_seeds.join('\n'));
    setExcludeText(feedConfig.ahrefs_exclude_terms.join(', '));
    setAutoRefresh(feedConfig.auto_refresh_enabled);
    setCountry(feedConfig.ahrefs_country || 'us');
    setTargetCount(feedConfig.ahrefs_target_count || 30);
    setPerSeedLimit(feedConfig.ahrefs_per_seed_limit || 50);
    setMinVolume(feedConfig.ahrefs_min_volume || 0);
    setMaxDifficulty(
      typeof feedConfig.ahrefs_max_difficulty === 'number'
        ? String(feedConfig.ahrefs_max_difficulty)
        : '',
    );
  }, [feedConfig]);

  const seedList = useMemo(() => parseKeywordList(seedText), [seedText]);
  const excludeTerms = useMemo(() => parseKeywordList(excludeText), [excludeText]);

  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    const feedItems = data?.items ?? [];
    if (!q) return feedItems;
    return feedItems.filter((item) => {
      const haystack = [
        item.primary_keyword,
        item.map_name ?? '',
        item.secondary_keywords.join(' '),
        item.filter_reason ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [data?.items, search]);

  const counts = useMemo(() => {
    const feedItems = data?.items ?? [];
    return {
      clear: feedItems.filter((item) => item.duplicate_status === 'clear').length,
      blocked: feedItems.filter((item) => item.duplicate_status !== 'clear').length,
      approved: feedItems.filter((item) => item.status === 'approved').length,
    };
  }, [data?.items]);

  const vettableItems = useMemo(() => selectKeywordVettingItems(items), [items]);

  const activeVettingStatus = vettingJobQuery.data?.status;
  const vettingInFlight = vettingMutation.isPending || Boolean(
    currentTenantVettingJob && (
      !activeVettingStatus ||
      ['queued', 'running', 'cancel_requested'].includes(activeVettingStatus)
    ),
  );

  const isBusy =
    refreshMutation.isPending ||
    ahrefsRefreshMutation.isPending ||
    configMutation.isPending ||
    updateMutation.isPending ||
    generateMutation.isPending ||
    vettingInFlight;

  useEffect(() => {
    const job = vettingJobQuery.data;
    if (!job || ['queued', 'running', 'cancel_requested'].includes(job.status)) return;
    const terminalKey = `${job.job_id}:${job.status}`;
    if (reportedVettingTerminalRef.current === terminalKey) return;
    reportedVettingTerminalRef.current = terminalKey;

    const countsText = `${job.agree_count} agree, ${job.weak_count} weak, ${job.swap_count} swap`;
    if (job.status === 'succeeded') {
      toast.success(`Keyword funnel complete: ${countsText}.`);
    } else if (job.status === 'partial') {
      toast.warning(`Keyword funnel partially complete: ${countsText}; ${job.items_failed} failed.`);
    } else {
      toast.error(`Keyword funnel ${job.status}: ${job.items_failed} of ${job.items_total} failed.`);
    }
    setActiveVettingJob((activeJob) =>
      activeJob?.jobId === job.job_id ? null : activeJob,
    );
  }, [vettingJobQuery.data]);

  useEffect(() => {
    if (!vettingJobQuery.error || !currentTenantVettingJob) return;
    toast.error(`Keyword vetting status unavailable: ${getKeywordVettingErrorMessage(vettingJobQuery.error)}`);
    setActiveVettingJob((job) =>
      job?.jobId === currentTenantVettingJob.jobId &&
      job.tenantId === currentTenantVettingJob.tenantId
        ? null
        : job,
    );
  }, [currentTenantVettingJob, vettingJobQuery.error]);

  const handleRefresh = async () => {
    if (!tenantId) return;
    try {
      const result = await refreshMutation.mutateAsync({
        tenantId,
        data: { include_skipped: includeSkipped, max_items: 100 },
      });
      const indexNote = result.index_auto_sync_attempted
        ? `, index checked (${result.internal_pages_index_count} pages)`
        : '';
      toast.success(
        `Feed refreshed: ${result.inserted} new, ${result.updated} updated, ${result.skipped} filtered${indexNote}`,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to refresh keyword feed');
    }
  };

  const handleSaveAhrefsConfig = async () => {
    if (!tenantId) return;
    const maxDifficultyValue = maxDifficulty.trim() ? Number(maxDifficulty) : null;
    try {
      await configMutation.mutateAsync({
        tenantId,
        data: {
          ...effectiveConfig,
          auto_refresh_enabled: autoRefresh,
          ahrefs_seeds: seedList,
          ahrefs_country: country.trim() || 'us',
          ahrefs_target_count: targetCount,
          ahrefs_per_seed_limit: perSeedLimit,
          ahrefs_min_volume: minVolume,
          ahrefs_max_difficulty: maxDifficultyValue,
          ahrefs_exclude_terms: excludeTerms,
        },
      });
      toast.success('Keyword feed config saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save feed config');
    }
  };

  const handleVetKeywords = async () => {
    if (!tenantId || vettableItems.length === 0) return;
    try {
      const job = await vettingMutation.mutateAsync({
        tenantId,
        feedItemIds: vettableItems.map((item) => item.id),
      });
      reportedVettingTerminalRef.current = null;
      setActiveVettingJob({ jobId: job.job_id, tenantId: job.tenant_id });
      const warningText = job.warnings.length
        ? ` (${job.warnings.length} skipped with warnings)`
        : '';
      toast.success(`Queued ${job.feed_item_ids.length} candidates for vetting and filtering${warningText}.`);
    } catch (error) {
      toast.error(`Keyword vetting was not queued: ${getKeywordVettingErrorMessage(error)}`);
    }
  };

  const handleAhrefsRefresh = async () => {
    if (!tenantId) return;
    if (seedList.length === 0) {
      toast.error('Add at least one Ahrefs seed');
      return;
    }

    const maxDifficultyValue = maxDifficulty.trim() ? Number(maxDifficulty) : null;
    try {
      const result = await ahrefsRefreshMutation.mutateAsync({
        tenantId,
        data: {
          seeds: seedList,
          target_count: targetCount,
          per_seed_limit: perSeedLimit,
          country: country.trim() || 'us',
          exclude_terms: excludeTerms,
          min_volume: minVolume,
          max_difficulty: maxDifficultyValue,
          similarity_threshold: effectiveConfig.similarity_threshold,
          dry_run: ahrefsDryRun,
          include_skipped: includeSkipped,
        },
      });
      const action = result.dry_run ? 'previewed' : 'updated';
      toast.success(
        `Ahrefs feed ${action}: ${result.selected_count} selected, ${result.skipped_duplicate_count} duplicates`,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to refresh from Ahrefs');
    }
  };

  const handleStatus = async (item: KeywordFeedItem, nextStatus: KeywordFeedStatus) => {
    if (!tenantId) return;
    setLoadingItemId(item.id);
    try {
      await updateMutation.mutateAsync({
        tenantId,
        itemId: item.id,
        data: { status: nextStatus },
      });
      toast.success(`${item.primary_keyword} ${nextStatus}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update feed item');
    } finally {
      setLoadingItemId(undefined);
    }
  };

  const handleMonthChange = async (item: KeywordFeedItem, value: string) => {
    if (!tenantId) return;
    try {
      await updateMutation.mutateAsync({
        tenantId,
        itemId: item.id,
        data: { target_month: value },
      });
      const label = formatTargetMonth(value) ?? value;
      toast.success(`${item.primary_keyword} scheduled for ${label}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update content month');
    }
  };

  const handleApprove = (item: KeywordFeedItem) => {
    if (item.hermes_verdict === 'swap') {
      const alt = item.hermes_suggested_swap || 'an alternative keyword';
      const proceed = window.confirm(
        `Codex flagged likely cannibalization and suggests "${alt}" — approve anyway?`,
      );
      if (!proceed) return;
    }
    void handleStatus(item, 'approved');
  };

  const handleGenerate = async (item: KeywordFeedItem) => {
    if (!tenantId) return;
    setLoadingItemId(item.id);
    try {
      await generateMutation.mutateAsync({
        tenantId,
        itemId: item.id,
        data: {
          wordpress_publish_mode: 'none',
          generation_mode: 'suggestion',
          model_override: FEED_WRITER_MODEL,
          // Omitted entirely when unset, so the backend default still applies.
          ...(selectedTemplateId ? { template_id: selectedTemplateId } : {}),
        },
      });
      toast.success(`Queued ${item.primary_keyword}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate from feed');
    } finally {
      setLoadingItemId(undefined);
    }
  };

  return (
    <div style={{ padding: '18px 28px 28px' }}>
      {/* Five seconds on open, naming the components an agent can drive. */}
      <AgentSurfaceIntro surfaceKey="feed" className="mb-3" />
      {/* Bottom-left, and only ever visible during real work. */}
      <AgentActivityDock />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 14,
        }}
      >
        <button
          type="button"
          onClick={handleRefresh}
          disabled={!tenantId || refreshMutation.isPending}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            padding: '8px 12px',
            borderRadius: 8,
            background: 'var(--color-coral)',
            border: '1px solid var(--color-coral)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 650,
            cursor: refreshMutation.isPending ? 'wait' : 'pointer',
          }}
        >
          {refreshMutation.isPending ? (
            <Loader2 size={15} strokeWidth={2} className="animate-spin" />
          ) : (
            <RefreshCw size={15} strokeWidth={2} />
          )}
          Refresh Feed
        </button>

        <button
          type="button"
          onClick={() => void handleVetKeywords()}
          disabled={!tenantId || vettableItems.length === 0 || isBusy}
          title={vettableItems.length === 0
            ? 'No loaded clear, unvetted candidates'
            : 'Score the narrowed candidate set; nothing is approved or removed automatically'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            padding: '8px 12px',
            borderRadius: 8,
            background: vettableItems.length === 0 || isBusy
              ? 'var(--color-paper-2)'
              : 'var(--color-teal-light)',
            border: '1px solid var(--color-teal)',
            color: vettableItems.length === 0 || isBusy
              ? 'var(--color-ink-3)'
              : 'var(--color-teal-dark)',
            fontSize: 13,
            fontWeight: 650,
            cursor: vettingInFlight ? 'wait' : 'pointer',
          }}
        >
          {vettingInFlight ? (
            <Loader2 size={15} strokeWidth={2} className="animate-spin" />
          ) : (
            <Check size={15} strokeWidth={2} />
          )}
          Vet &amp; filter {vettableItems.length}
        </button>

        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value as '' | KeywordFeedStatus)}
          style={{
            height: 34,
            borderRadius: 7,
            border: '1px solid var(--color-warm-border)',
            background: 'var(--color-paper)',
            color: 'var(--color-ink-2)',
            fontSize: 12,
            padding: '0 9px',
          }}
        >
          {STATUS_LABELS.map((option) => (
            <option key={option.value || 'all'} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <select
          aria-label="Template"
          value={selectedTemplateId}
          onChange={(event) => setSelectedTemplateId(event.target.value)}
          disabled={generateMutation.isPending}
          title="Recipe used when you press Generate on a row"
          style={{
            height: 34,
            borderRadius: 7,
            border: '1px solid var(--color-warm-border)',
            background: 'var(--color-paper)',
            color: 'var(--color-ink-2)',
            fontSize: 12,
            padding: '0 9px',
          }}
        >
          <option value="">Default (backend)</option>
          {templatesLoading && (
            <option value="__loading" disabled>
              Loading templates…
            </option>
          )}
          {templatesError && (
            <option value="__error" disabled>
              Templates unavailable
            </option>
          )}
          {templateOptions.map((template) => (
            <option key={template.id} value={template.id}>
              {template.name}
            </option>
          ))}
        </select>

        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            color: 'var(--color-ink-2)',
            fontSize: 12,
          }}
        >
          <input
            type="checkbox"
            checked={includeSkipped}
            onChange={(event) => setIncludeSkipped(event.target.checked)}
          />
          Show skipped
        </label>

        <div style={{ flex: 1 }} />

        <span style={{ color: 'var(--color-ink-3)', fontSize: 12 }}>
          {counts.clear} clear / {counts.blocked} filtered / {counts.approved} approved
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(260px, 1.3fr) minmax(220px, 1fr) repeat(5, minmax(76px, 96px)) 150px',
          gap: 10,
          alignItems: 'end',
          marginBottom: 14,
          padding: 12,
          border: '1px solid var(--color-warm-border)',
          borderRadius: 8,
          background: 'var(--color-paper)',
          overflowX: 'auto',
        }}
      >
        <label style={{ display: 'grid', gap: 5, minWidth: 260 }}>
          <span style={{ color: 'var(--color-ink-3)', fontSize: 11, fontWeight: 700 }}>
            Ahrefs seeds
          </span>
          <textarea
            value={seedText}
            onChange={(event) => setSeedText(event.target.value)}
            rows={2}
            style={{
              width: '100%',
              resize: 'vertical',
              minHeight: 58,
              borderRadius: 7,
              border: '1px solid var(--color-warm-border)',
              background: 'var(--color-paper-2)',
              color: 'var(--color-ink)',
              fontSize: 12,
              padding: '8px 9px',
            }}
          />
        </label>

        <label style={{ display: 'grid', gap: 5, minWidth: 220 }}>
          <span style={{ color: 'var(--color-ink-3)', fontSize: 11, fontWeight: 700 }}>
            Exclude
          </span>
          <input
            type="text"
            value={excludeText}
            onChange={(event) => setExcludeText(event.target.value)}
            style={{
              height: 34,
              borderRadius: 7,
              border: '1px solid var(--color-warm-border)',
              background: 'var(--color-paper-2)',
              color: 'var(--color-ink)',
              fontSize: 12,
              padding: '0 9px',
            }}
          />
        </label>

        <label style={{ display: 'grid', gap: 5 }}>
          <span style={{ color: 'var(--color-ink-3)', fontSize: 11, fontWeight: 700 }}>
            Country
          </span>
          <input
            type="text"
            value={country}
            onChange={(event) => setCountry(event.target.value)}
            style={{
              height: 34,
              borderRadius: 7,
              border: '1px solid var(--color-warm-border)',
              background: 'var(--color-paper-2)',
              color: 'var(--color-ink)',
              fontSize: 12,
              padding: '0 9px',
            }}
          />
        </label>

        <label style={{ display: 'grid', gap: 5 }}>
          <span style={{ color: 'var(--color-ink-3)', fontSize: 11, fontWeight: 700 }}>
            Count
          </span>
          <input
            type="number"
            min={1}
            max={100}
            value={targetCount}
            onChange={(event) => setTargetCount(Number(event.target.value))}
            style={{
              height: 34,
              borderRadius: 7,
              border: '1px solid var(--color-warm-border)',
              background: 'var(--color-paper-2)',
              color: 'var(--color-ink)',
              fontSize: 12,
              padding: '0 9px',
            }}
          />
        </label>

        <label style={{ display: 'grid', gap: 5 }}>
          <span style={{ color: 'var(--color-ink-3)', fontSize: 11, fontWeight: 700 }}>
            Per seed
          </span>
          <input
            type="number"
            min={10}
            max={100}
            value={perSeedLimit}
            onChange={(event) => setPerSeedLimit(Number(event.target.value))}
            style={{
              height: 34,
              borderRadius: 7,
              border: '1px solid var(--color-warm-border)',
              background: 'var(--color-paper-2)',
              color: 'var(--color-ink)',
              fontSize: 12,
              padding: '0 9px',
            }}
          />
        </label>

        <label style={{ display: 'grid', gap: 5 }}>
          <span style={{ color: 'var(--color-ink-3)', fontSize: 11, fontWeight: 700 }}>
            Min vol
          </span>
          <input
            type="number"
            min={0}
            value={minVolume}
            onChange={(event) => setMinVolume(Number(event.target.value))}
            style={{
              height: 34,
              borderRadius: 7,
              border: '1px solid var(--color-warm-border)',
              background: 'var(--color-paper-2)',
              color: 'var(--color-ink)',
              fontSize: 12,
              padding: '0 9px',
            }}
          />
        </label>

        <label style={{ display: 'grid', gap: 5 }}>
          <span style={{ color: 'var(--color-ink-3)', fontSize: 11, fontWeight: 700 }}>
            Max KD
          </span>
          <input
            type="number"
            min={0}
            max={100}
            value={maxDifficulty}
            onChange={(event) => setMaxDifficulty(event.target.value)}
            style={{
              height: 34,
              borderRadius: 7,
              border: '1px solid var(--color-warm-border)',
              background: 'var(--color-paper-2)',
              color: 'var(--color-ink)',
              fontSize: 12,
              padding: '0 9px',
            }}
          />
        </label>

        <div style={{ display: 'grid', gap: 8, minWidth: 150 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              color: 'var(--color-ink-2)',
              fontSize: 12,
            }}
          >
            <span>Auto</span>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(event) => setAutoRefresh(event.target.checked)}
            />
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              color: 'var(--color-ink-2)',
              fontSize: 12,
            }}
          >
            <span>Preview</span>
            <input
              type="checkbox"
              checked={ahrefsDryRun}
              onChange={(event) => setAhrefsDryRun(event.target.checked)}
            />
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            <IconButton
              label="Save feed config"
              disabled={!tenantId || configMutation.isPending}
              onClick={handleSaveAhrefsConfig}
            >
              {configMutation.isPending ? (
                <Loader2 size={15} strokeWidth={2} className="animate-spin" />
              ) : (
                <Save size={15} strokeWidth={2} />
              )}
            </IconButton>
            <IconButton
              label="Run Ahrefs refresh"
              disabled={!tenantId || ahrefsRefreshMutation.isPending || seedList.length === 0}
              onClick={handleAhrefsRefresh}
            >
              {ahrefsRefreshMutation.isPending ? (
                <Loader2 size={15} strokeWidth={2} className="animate-spin" />
              ) : (
                <RefreshCw size={15} strokeWidth={2} />
              )}
            </IconButton>
          </div>
        </div>
      </div>

      <div
        style={{
          overflow: 'hidden',
          overflowX: 'auto',
          border: '1px solid var(--color-warm-border)',
          borderRadius: 8,
          background: 'var(--color-paper)',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(240px, 2fr) 100px 88px 88px 128px 118px 120px',
            gap: 14,
            minWidth: 980,
            padding: '9px 16px',
            borderBottom: '1px solid var(--color-warm-border)',
            background: 'var(--color-warm-bg)',
            color: 'var(--color-ink-3)',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          <div>Keyword</div>
          <div>Score</div>
          <div>Volume</div>
          <div>KD</div>
          <div>Status</div>
          <div>Month</div>
          <div style={{ textAlign: 'right' }}>Actions</div>
        </div>

        {isLoading && (
          <div style={{ padding: 28, color: 'var(--color-ink-3)', fontSize: 13 }}>
            Loading feed
          </div>
        )}

        {isError && (
          <div style={{ padding: 28, color: '#b91c1c', fontSize: 13 }}>
            Failed to load keyword feed
            <button
              type="button"
              onClick={() => refetch()}
              style={{
                marginLeft: 10,
                color: 'var(--color-coral-dark)',
                fontWeight: 700,
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <div style={{ padding: 28, color: 'var(--color-ink-3)', fontSize: 13 }}>
            No feed items
          </div>
        )}

        {!isLoading &&
          !isError &&
          items.map((item) => (
            <FeedRow
              key={item.id}
              item={item}
              busy={isBusy}
              loadingItemId={loadingItemId}
              onApprove={handleApprove}
              onReject={(row) => handleStatus(row, 'rejected')}
              onGenerate={handleGenerate}
              onMonthChange={handleMonthChange}
            />
          ))}
      </div>
    </div>
  );
};

export default KeywordFeedPanel;
